import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, ChevronRight, CheckCircle2, Brain, Zap, Rocket, Wand2, Target, ChevronDown, Check } from 'lucide-react';

const TASK_ICONS = [Rocket, Wand2, Zap, Brain, Sparkles, Target];

export const TaskSection = ({ 
  dayNumber,
  dayTitle,
  dayContext,
  tasks = [],
  completedTasks = [],
  onStartTask,
  onOpenMentor,
  availableDays = [],
  selectedDay,
  onDayChange
}) => {
  const [showDayPicker, setShowDayPicker] = useState(false);
  const pickerRef = useRef(null);
  const hasMultipleDays = availableDays.length > 1;

  // Close picker on outside click
  useEffect(() => {
    if (!showDayPicker) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowDayPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showDayPicker]);

  const completed = completedTasks.filter(id => tasks.some(t => t.id === id)).length;
  const currentAvail = availableDays.find(d => d.isCurrent);

  return (
    <div data-testid="tasks-section">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' }}>
            <Brain className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-white tracking-tight">Twoja praktyka</p>
            <p className="text-[10px] text-slate-500">{completed}/{tasks.length} ukończonych</p>
          </div>
        </div>

        {/* Day selector button */}
        {hasMultipleDays && (
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => setShowDayPicker(!showDayPicker)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                boxShadow: '0 2px 8px rgba(14,165,233,0.25)',
              }}
              data-testid="day-selector-btn"
            >
              <span className="text-white">Dzień {selectedDay}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-white/80 transition-transform ${showDayPicker ? 'rotate-180' : ''}`} />
            </button>

            {/* Day picker dropdown */}
            {showDayPicker && (
              <div
                className="absolute right-0 top-full mt-2 z-50 rounded-xl overflow-hidden"
                style={{
                  background: '#0f1a2e',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                  minWidth: '220px',
                }}
                data-testid="day-picker-dropdown"
              >
                <div className="px-3 py-2.5 border-b border-white/[0.06]">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Wybierz dzień</p>
                </div>
                <div className="p-2 grid grid-cols-5 gap-1 max-h-[240px] overflow-y-auto">
                  {availableDays.map((day) => {
                    const isActive = day.number === selectedDay;
                    const isCurrent = day.isCurrent;
                    return (
                      <button
                        key={day.number}
                        onClick={() => { onDayChange(day.number); setShowDayPicker(false); }}
                        data-testid={`day-pick-${day.number}`}
                        className={`relative w-full aspect-square rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                          isActive
                            ? 'text-white'
                            : 'text-slate-400 hover:text-white hover:bg-white/[0.06]'
                        }`}
                        style={isActive ? {
                          background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                          boxShadow: '0 2px 8px rgba(14,165,233,0.3)',
                        } : {}}
                      >
                        {day.number}
                        {isCurrent && !isActive && (
                          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-sky-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Day context subtitle */}
      {hasMultipleDays && (
        <p className="text-[11px] text-slate-500 mb-2 px-1">{dayTitle}</p>
      )}

      {/* Task cards */}
      <div className="space-y-2">
        {tasks.length > 0 ? tasks.map((task, idx) => {
          const isCompleted = completedTasks.includes(task.id);
          const Icon = TASK_ICONS[idx % TASK_ICONS.length];
          const cleanTitle = task.title.replace(/^[^\s]+\s/, '');
          
          return (
            <button
              key={task.id}
              onClick={() => onStartTask(task)}
              className="w-full rounded-xl p-4 flex items-center gap-3.5 transition-all group text-left"
              style={{ 
                background: isCompleted ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                border: isCompleted ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(255,255,255,0.06)',
              }}
              data-testid={`task-${task.id}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                isCompleted 
                  ? 'bg-emerald-500/15' 
                  : 'bg-sky-500/10 group-hover:bg-sky-500/20'
              }`}>
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Icon className="w-5 h-5 text-sky-400" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold leading-snug ${isCompleted ? 'text-slate-500' : 'text-white'}`}>
                  {cleanTitle}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{task.description}</p>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                {!isCompleted && (
                  <span className="text-[10px] font-semibold text-sky-500 uppercase tracking-wider hidden sm:inline">
                    Rozpocznij
                  </span>
                )}
                <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-0.5 ${
                  isCompleted ? 'text-slate-700' : 'text-slate-500 group-hover:text-sky-400'
                }`} />
              </div>
            </button>
          );
        }) : (
          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <p className="text-xs text-slate-500">Brak zadań dla tego dnia</p>
          </div>
        )}
      </div>

      {/* Free mentor conversation — premium block */}
      <button
        onClick={onOpenMentor}
        className="w-full mt-4 rounded-xl overflow-hidden transition-all group text-left"
        style={{ 
          background: 'linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(6,182,212,0.10) 100%)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(14,165,233,0.18)',
          boxShadow: '0 4px 32px rgba(14,165,233,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
        }}
        data-testid="open-free-mentor"
      >
        <div className="px-5 py-6 sm:py-7 flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)', boxShadow: '0 6px 24px rgba(14,165,233,0.35)' }}>
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-white group-hover:text-sky-300 transition-colors leading-snug">
              Rozmowa z Mentorem
            </p>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">Zadaj pytanie, przetestuj pomysł lub poproś o analizę — w kontekście dnia {dayNumber}</p>
          </div>
          <ChevronRight className="w-6 h-6 text-slate-500 group-hover:text-sky-400 transition-all group-hover:translate-x-1 flex-shrink-0" />
        </div>
      </button>
    </div>
  );
};

export default TaskSection;
